import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import ShopAuthLayout, { ArrowRightIcon } from './ShopAuthLayout';
import { getShopAuthState, loginShopAccount } from '../../utils/shopAuthApi';

function Field({ id, label, ...props }) {
    return (
        <label className="block" htmlFor={id}>
            <span className="text-[11px] font-black uppercase tracking-[0.18em] text-[#606060]">{label}</span>
            <input
                id={id}
                className="mt-2 h-12 w-full rounded-xl border border-[#d8cbbd] bg-[#fbfaf8] px-4 text-[15px] font-semibold text-[#414141] shadow-[0_2px_7px_rgba(35,31,25,0.12)] outline-none transition placeholder:font-medium placeholder:text-[#a9a9a9] focus:border-[#D81F26] focus:ring-4 focus:ring-[#D81F26]/10 sm:text-base"
                {...props}
            />
        </label>
    );
}

export default function ShopLoginPage() {
    const navigate = useNavigate();
    const location = useLocation();
    // Set by the reset flow, which deliberately does not sign the partner in: typing
    // the new password once here proves it took.
    const justResetPassword = Boolean(location.state?.passwordReset);
    const [identifier, setIdentifier] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isChecking, setIsChecking] = useState(true);

    const nextPath = (() => {
        const value = new URLSearchParams(location.search).get('next') || '/';
        return value.startsWith('/') && !value.startsWith('//') ? value : '/';
    })();

    useEffect(() => {
        let cancelled = false;
        getShopAuthState()
            .then((data) => {
                if (!cancelled && data?.authenticated) navigate(nextPath, { replace: true });
            })
            .catch(() => {})
            .finally(() => {
                if (!cancelled) setIsChecking(false);
            });
        return () => {
            cancelled = true;
        };
    }, [navigate, nextPath]);

    const handleSubmit = async (event) => {
        event.preventDefault();
        setError('');
        setIsSubmitting(true);
        try {
            await loginShopAccount({ identifier, password });
            navigate(nextPath, { replace: true });
        } catch (err) {
            setError(err?.message || 'Could not sign in.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <ShopAuthLayout>
            <p className="text-[10px] font-black uppercase tracking-[0.22em] text-[#D81F26] sm:text-[11px]">Shop portal access</p>
            <h1 className="mt-1.5 text-2xl font-black leading-tight tracking-tight text-[#414141] sm:mt-2 sm:text-[28px]">Welcome back.</h1>
            <p className="mt-2 text-[13px] font-semibold leading-relaxed text-[#606060] sm:mt-2.5 sm:text-sm">Sign in to pick up where your team left off.</p>

            {justResetPassword && !error ? (
                <p className="mt-5 rounded-xl border border-[#d8cbbd] bg-[#fbfaf8] px-4 py-2.5 text-[13px] font-bold text-[#606060]">
                    Password updated. Sign in with your new one.
                </p>
            ) : null}

            <form className="mt-7 space-y-5 sm:mt-8" onSubmit={handleSubmit}>
                <Field
                    id="shop-login-identifier"
                    label="Work email or phone"
                    autoComplete="username"
                    placeholder="you@shop.com"
                    value={identifier}
                    onChange={(event) => setIdentifier(event.target.value)}
                    disabled={isChecking || isSubmitting}
                    required
                />

                <div>
                    <div className="mb-2 flex items-center justify-between gap-4">
                        <label className="text-[11px] font-black uppercase tracking-[0.18em] text-[#606060]" htmlFor="shop-login-password">Password</label>
                        <Link className="text-[11px] font-black text-[#D81F26] transition hover:text-[#414141]" to="/forgot-password">Forgot password?</Link>
                    </div>
                    <input
                        id="shop-login-password"
                        className="h-12 w-full rounded-xl border border-[#d8cbbd] bg-[#fbfaf8] px-4 text-[15px] font-semibold text-[#414141] shadow-[0_2px_7px_rgba(35,31,25,0.12)] outline-none transition placeholder:font-medium placeholder:text-[#a9a9a9] focus:border-[#D81F26] focus:ring-4 focus:ring-[#D81F26]/10 sm:text-base"
                        type="password"
                        autoComplete="current-password"
                        placeholder="Your password"
                        value={password}
                        onChange={(event) => setPassword(event.target.value)}
                        disabled={isChecking || isSubmitting}
                        required
                    />
                </div>

                {error ? (
                    <p className="rounded-xl border border-red-200 bg-red-50 px-4 py-2.5 text-[13px] font-bold text-red-700">{error}</p>
                ) : null}

                <button
                    type="submit"
                    disabled={isChecking || isSubmitting}
                    className="flex h-12 w-full items-center justify-center gap-2.5 rounded-xl bg-[#D81F26] px-5 text-[12px] font-black uppercase tracking-[0.16em] text-white transition hover:bg-[#B8181E] active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-60 sm:text-[11px]"
                >
                    {isSubmitting ? 'Signing in...' : 'Sign in'}
                    <ArrowRightIcon className="h-4 w-4" />
                </button>
            </form>

            <p className="mt-7 text-center text-[12px] font-semibold text-[#606060]">
                New to CarFido Partner? <Link className="font-black text-[#D81F26] transition hover:text-[#414141]" to="/signup">Create a shop account</Link>
            </p>
        </ShopAuthLayout>
    );
}
